import { can } from '../../shared/perms';
import { PmDuePanel } from './Pm';
import { useApi, dollars, navigate, ago, fmtClock, WO_STATUSES, currentMe, type Me, type Row } from '../lib';
import { TodayTarget, MonthProgress } from './Money';
import { TeamCard } from './Performance';
import { Stat, Panel, Table, Badge, Loading, ErrorBox, Link, Icon, Empty } from '../ui';

function OwnerMoney() {
  const { data: m } = useApi<Row>('/api/money/overview');
  if (!m) return null;
  return (
    <div className="money-strip">
      <TodayTarget d={m} />
      <div className="money-cards">
        <button className="money-card" onClick={() => navigate('/money')}>
          <small>Net profit this month so far</small>
          <strong className={m.netProfit < 0 ? 'danger' : ''}>{dollars(m.netProfit)}</strong>
          <MonthProgress d={m} />
        </button>
        <button className="money-card" onClick={() => navigate('/money?tab=bills')}>
          <small>Monthly costs</small>
          <strong>{dollars(m.costs.monthCosts)}</strong>
          <span className="muted">Bills {dollars(m.costs.fixed)} · Staff {dollars(m.costs.staffEstimate)}{m.costs.other ? ` · Other ${dollars(m.costs.other)}` : ''}</span>
          <span className="muted">{dollars(m.costs.dailyTarget)} per work day on average{m.costs.staffEstimate !== m.costs.staffPlanned ? ` · staff ${m.costs.staffEstimate < m.costs.staffPlanned ? '−' : '+'}${dollars(Math.abs(m.costs.staffEstimate - m.costs.staffPlanned))} from attendance` : ''}</span>
        </button>
        <button className="money-card" onClick={() => navigate('/money?tab=suppliers')}>
          <small>Money in parts</small>
          <strong>{dollars(m.inventory.value)}</strong>
          <span className="muted">on your shelves (at cost)</span>
          {m.credit.onShelf > 0 && <span className="muted"><b>{dollars(m.credit.onShelf)}</b> of it not paid yet (on credit)</span>}
        </button>
        <SupplierCard c={m.credit} />
      </div>
    </div>
  );
}

/** What the shop owes its parts suppliers: already used on trucks vs still on the shelf, past due, and this month's movement. */
function SupplierCard({ c }: { c: Row }) {
  const used = c.owed ? Math.round((c.usedUnpaid / c.owed) * 100) : 0;
  return (
    <button className={'money-card' + (c.overdue ? ' warn' : '')} onClick={() => navigate('/money?tab=suppliers')}>
      <small>You owe suppliers</small>
      <strong>{dollars(c.owed)}</strong>
      {c.owed > 0 && <div className="owe-bar" aria-hidden="true"><i style={{ width: used + '%' }} /></div>}
      {c.owed > 0 && <span className="muted"><i className="owe-dot used" /><b>{dollars(c.usedUnpaid)}</b> already used on trucks</span>}
      {c.owed > 0 && <span className="muted"><i className="owe-dot shelf" /><b>{dollars(c.onShelf)}</b> still on the shelf</span>}
      {c.overdue > 0 && <span className="warn-text">{dollars(c.overdue)} past due · {c.overdueBills} bill{c.overdueBills === 1 ? '' : 's'}</span>}
      {(c.owed > 0 || c.monthBought > 0 || c.monthPaid > 0) && <span className="muted small">From earlier months {dollars(Math.max(0, c.earlier))} · bought on credit this month {dollars(c.monthBought)} · paid {dollars(c.monthPaid)}</span>}
      {c.owed === 0 && <span className="muted">Nothing owed — all supplier bills paid</span>}
      <span className="linkish">Pay suppliers →</span>
    </button>
  );
}

export function Dashboard({ me }: { me: Me }) {
  const { data: d, error, reload } = useApi<Row>('/api/dashboard');
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const count = (s: string) => (d.byStatus as Row[]).find(x => x.status === s)?.n || 0;
  const open = WO_STATUSES.filter(s => s !== 'Completed').reduce((a, s) => a + count(s), 0);
  const unbilledValue = (d.unbilled as Row[]).reduce((a, w) => a + w.parts_value + w.labor_value, 0);
  const alerts = [
    d.unbilled.length > 0 && { tone: 'red', text: `${d.unbilled.length} finished job${d.unbilled.length > 1 ? 's' : ''} not invoiced yet — ${dollars(unbilledValue)}`, to: '/work-orders?view=to_invoice' },
    d.toCheck > 0 && { tone: 'amber', text: `${d.toCheck} finished job${d.toCheck > 1 ? 's' : ''} waiting for the supervisor's check`, to: '/checks' },
    d.openPhotos > 0 && { tone: 'amber', text: `${d.openPhotos} part photo${d.openPhotos > 1 ? 's' : ''} from mechanics waiting for review`, to: '/inventory?tab=leaks' },
    d.negative.length > 0 && { tone: 'amber', text: `${d.negative.length} part${d.negative.length > 1 ? 's' : ''} below zero on hand — count the shelf`, to: '/inventory?tab=leaks' },
    d.shrink.total > 0 && { tone: 'red', text: `${dollars(d.shrink.total)} in parts missing from shelf counts (last 30 days)`, to: '/inventory?tab=leaks' },
    d.priceChanges?.length > 0 && { tone: 'red', text: `Vendor price changed on ${d.priceChanges.map((p: Row) => `${p.number} (${p.vendor})`).join(', ')} — check your sell prices`, to: '/inventory?tab=orders&status=received' },
    d.receipts?.pending > 0 && { tone: 'amber', text: `${d.receipts.pending} store receipt${d.receipts.pending > 1 ? 's' : ''} from mechanics waiting for your OK`, to: '/money?tab=purchases' },
    d.receipts?.owed > 0 && { tone: 'blue', text: `${dollars(d.receipts.owed)} owed back to workers who paid with their own money`, to: '/money?tab=purchases' },
    d.returns?.n > 0 && { tone: d.returns.oldest && (Date.now() - Date.parse(d.returns.oldest)) / 864e5 >= Number(currentMe()?.shop.return_alert_days || 14) ? 'red' : 'amber',
      text: `${d.returns.n} part${d.returns.n > 1 ? 's' : ''} to return to vendors — ${dollars(d.returns.value)}${d.returns.oldest ? ` (oldest ${Math.floor((Date.now() - Date.parse(d.returns.oldest)) / 864e5)} days)` : ''}`, to: '/inventory?tab=cores' },
    d.low.length > 0 && { tone: 'blue', text: `${d.low.length} part${d.low.length > 1 ? 's' : ''} at or below reorder point`, to: '/inventory?filter=low' },
  ].filter(Boolean) as { tone: string; text: string; to: string }[];

  return (
    <>
      <div className="page-head">
        <div><h1>Dashboard</h1><p>Every bay, every job, every dollar.</p></div>
        <div className="actions"><button className="btn primary" onClick={() => navigate('/work-orders?new=1')}><Icon name="plus" size={16} />New work order</button></div>
      </div>

      {alerts.length > 0 && (
        <div className="alerts">
          {alerts.map(a => <button key={a.text} className={'alert ' + a.tone} onClick={() => navigate(a.to)}><Icon name="alert" size={16} />{a.text}<span>Review →</span></button>)}
        </div>
      )}

      {can(me.user, 'money') && <OwnerMoney />}
      <PmDuePanel />

      <div className="stats">
        <Stat label="Open jobs" value={open} hint={`${count('Waiting Parts')} waiting parts · ${count('Ready')} ready`} onClick={() => navigate('/work-orders')} />
        <Stat label="Invoiced this month" value={dollars(d.month.total)} hint={`${d.month.n} invoice${d.month.n === 1 ? '' : 's'}`} onClick={() => navigate('/invoices')} />
        <Stat label="Collected this month" value={dollars(d.received.total)} hint="Payments recorded" />
        <Stat label="Customers owe" value={dollars(d.ar.ar)} hint={d.ar.overdue ? <span className="danger">{dollars(d.ar.overdue)} overdue</span> : 'Nothing overdue'} tone={d.ar.overdue ? 'warn' : ''} onClick={() => navigate('/invoices?status=Unpaid')} />
        <Stat label="Parts on open jobs" value={dollars(d.openPartsValue.total)} hint="Put on trucks, not invoiced yet" onClick={() => navigate('/work-orders')} />
      </div>

      <div className="status-strip">
        {WO_STATUSES.filter(s => s !== 'Completed').map(s => (
          <button key={s} onClick={() => navigate('/work-orders?status=' + encodeURIComponent(s))}><Badge>{s}</Badge><b>{count(s)}</b></button>
        ))}
      </div>

      <div className="grid-2">
        <Panel title="Active jobs" action={<Link to="/work-orders" className="link">All work orders →</Link>}>
          <Table heads={['WO', 'Customer / Unit', 'Status', 'Tech', 'Updated']} empty="No open jobs."
            rows={(d.recent as Row[]).map(w => [
              <b>{w.number}{w.priority === 'Urgent' && <> <Badge>Urgent</Badge></>}</b>,
              <><div>{w.customer_name || <i className="muted">No customer</i>}</div><small className="muted">#{w.unit || '—'} · {w.complaint}</small></>,
              <Badge>{w.status}</Badge>, w.assigned_name || '—', ago(w.updated_at),
            ])}
            onRow={i => navigate('/work-orders/' + d.recent[i].id)} />
        </Panel>
        <div className="stack">
          {can(me.user, 'reports') && <TeamCard />}
          <Panel title="On the clock now">
            {d.clockedIn.length ? (
              <ul className="list">{(d.clockedIn as Row[]).map((t, i) => <li key={i}><span className="dot green" /><b>{t.name}</b><span className="muted">since {fmtClock(t.clock_in)}{t.number ? ' · ' + t.number : ''}</span></li>)}</ul>
            ) : <Empty>Nobody is clocked in.</Empty>}
          </Panel>
          <Panel title="Ready to invoice" action={d.unbilled.length ? <Link to="/work-orders?view=to_invoice" className="link">See all →</Link> : null}>
            {d.unbilled.length ? (
              <ul className="list">{(d.unbilled as Row[]).slice(0, 6).map(w => (
                <li key={w.id} className="clickable" onClick={() => navigate('/work-orders/' + w.id)}>
                  <b>{w.number}</b><span className="muted">{w.customer_name || 'No customer'}</span><span className="right">{dollars(w.parts_value + w.labor_value)}</span>
                </li>))}
              </ul>
            ) : <Empty>Everything finished has been invoiced.</Empty>}
          </Panel>
          <Panel title="Recent activity">
            <ul className="list small">{(d.activity as Row[]).map((a, i) => <li key={i}><span>{a.action}</span><span className="muted right">{a.name} · {ago(a.created_at)}</span></li>)}</ul>
          </Panel>
        </div>
      </div>
    </>
  );
}
